import DataTool from 'echarts/dist/extension/dataTool';
import { EChartService } from './echart.service';
import { StatsCompetencyBoxplot } from '@/models/statistics/stats-competency-boxplot';
import { Competency, CustomCompetency } from '@/models/competency/competency';
import { GEnum } from '@/constants/enum.defination';
import { NumberUtils } from '@/utils/number.utils';
import { StatisticsUtils } from '@/utils/statistics.utils';
import { AvgAndSdData, AvgAndSds, AvgAndSd } from '@/models/statistics/stats-boxplot-subtable-data';
import i18n from '@/i18n';

interface ScoreProperty {
  score: number;
  name: string;
  studentID: number;
  belongs: string;
}

interface BoxplotOtherCompetency {
  competencyID: number;
  name: string;
  colorCode: string;
}

export class CustomCompetencyBoxplotEchartService extends EChartService {
  private otherCompetencys: BoxplotOtherCompetency[] = [];

  get ohterCompetencyIDs(): number[] {
    return this.otherCompetencys.map((competency: BoxplotOtherCompetency) => competency.competencyID);
  }

  competencies: Competency[];
  competencyIDs: number[];
  self: any;
  val: number;
  customCompetencies: CustomCompetency[];
  subTableColmunsArray: any[];
  subTableDataArray: any[];
  subTableAvgAndSd: AvgAndSdData;

  constructor(competencies: Competency[] = [], customCompetencies: CustomCompetency[]) {
    super();
    this.series = [];
    this.labels = [];
    this.competencyIDs = competencies.map((competency: Competency) => competency.competencyID);
    this.competencies = competencies;
    this.customCompetencies = customCompetencies;
    this.subTableColmunsArray = [];
    this.subTableDataArray = [];
    this.subTableAvgAndSd = {};
  }

  get subTableColmuns(): any[] {
    return this.subTableColmunsArray;
  }

  get subTableData(): any[] {
    return this.subTableDataArray;
  }

  createSubTableColumns(nextNumber: number) {
    let cols: any[] = [];

    if (nextNumber > 1) {
      for (let i = 0; i < this.customCompetencies.length; i++) {
        for (let j = 1; j <= nextNumber; j++) {
          const prefix = `${this.serialNumberPrefix}-${j}`;
          cols.push({
            title: this.customCompetencies[i].name + `(${this.serialNumberPrefix}-${j})`,
            key: prefix + '_' + i,
            width: 160,
            align: 'center',
          });
        }
      }
    } else {
      const prefix = `${this.serialNumberPrefix}-${nextNumber}`;
      cols = this.customCompetencies.map((d: CustomCompetency, i: number) => {
        return {
          title: d.name,
          key: prefix + '_' + i,
          width: 160,
          align: 'center',
        };
      });
    }

    this.subTableColmunsArray = [
      {
        title: '-',
        key: 'name',
        width: 160,
        align: 'center',
      },
    ].concat(cols);
  }

  createSubTableData(data: any, nextNumber: number) {
    // {namae: "最大値", }    最大値  1列目
    // {namae: "75%", }       75%     2列目
    // {namae: "50%", }       50%     3列目
    // {namae: "25%", }       25%     4列目
    // {namae: "最小値", }    最小値  5列目
    // {namae: "効果量", }    効果量  6列目
    if (!this.subTableDataArray.length) {
      this.subTableDataArray = [
        { name: i18n.t('VALUE_MAX').toString() },
        { name: '75%' },
        { name: i18n.t('MEDIAN').toString() },
        { name: '25%' },
        { name: i18n.t('VALUE_MIN').toString() },
        { name: i18n.t('EXAMINEE_COUNT').toString() },
        { name: i18n.t('AVERAGE').toString() },
        { name: i18n.t('STANDARD_DEVIATION').toString() },
        { name: i18n.t('EFFECT_SIZE').toString() },
      ];
    }
    const prefix = `${this.serialNumberPrefix}-${nextNumber}`;
    data.boxData.forEach((box: any, i: number) => {
      if (box.length) {
        const key = prefix + '_' + i;
        const competency = this.customCompetencies[i];
        const nowSN = `${this.serialNumberPrefix}-${nextNumber}`;
        const nowAvgAndSd = this.subTableAvgAndSd[nowSN][competency.id];
        this.subTableDataArray[0][key] = box[4];
        this.subTableDataArray[1][key] = box[3];
        this.subTableDataArray[2][key] = box[2];
        this.subTableDataArray[3][key] = box[1];
        this.subTableDataArray[4][key] = box[0];
        this.subTableDataArray[5][key] = nowAvgAndSd.n;
        this.subTableDataArray[6][key] = this.percent(nowAvgAndSd.avg);
        this.subTableDataArray[7][key] = this.percent(nowAvgAndSd.sd);
        // 系列追加が2個め以降であれば、ひとつ前のデータと比較し効果量を算出する
        if (nextNumber > 1) {
          // ひとつ前のデータが undefined であれば算出しない
          const prevSN = `${this.serialNumberPrefix}-${nextNumber - 1}`;
          const prevAvgAndSd = this.subTableAvgAndSd[prevSN][competency.id];
          if (prevAvgAndSd) {
            const d = NumberUtils.floor(StatisticsUtils.cohen(prevAvgAndSd, nowAvgAndSd), 100);
            this.subTableDataArray[8][key] = d + StatisticsUtils.effectGuide(d);
          } else {
            this.subTableDataArray[8][key] = '';
          }
        } else {
          this.subTableDataArray[8][key] = '';
        }
      }
    });
  }

  get xAxisLabels(): any[] {
    //その他のコンピテンシー の場合は、competencysではなく、otherCompetencysを元にデータを作成する
    return this.otherCompetencys.map((d: BoxplotOtherCompetency) => {
      return {
        value: d.name,
        textStyle: {
          color: d.colorCode,
          fontSize: 12,
          fontWeight: 'bold',
          borderWidth: 2,
          borderRadius: 1,
        },
      };
    });
  }

  get options() {
    return {
      legend: {
        data: this.labels,
        left: 'center',
      },
      grid: {
        bottom: '25%',
      },
      xAxis: {
        type: 'category',
        data: this.xAxisLabels,
        boundaryGap: true,
        nameGap: 20,
        splitArea: {
          show: true,
        },
        axisLabel: {
          formatter: '{value}',
          rotate: 15,
        },
        splitLine: {
          show: false,
        },
      },
      yAxis: {
        min: 0,
        max: 100,
        type: 'value',
        name: i18n.t('COMPETENCY_SCORE').toString(),
      },
      dataZoom: [
        {
          type: 'inside',
          start: 0,
          end: 100,
        },
        {
          show: true,
          height: 20,
          type: 'slider',
          top: '90%',
          xAxisIndex: [0],
          start: 0,
          end: 100,
        },
      ],
      tooltip: {
        formatter: function(params: any) {
          return params.name;
        },
      },
      series: this.series,
    };
  }

  addSeries(statsCompetencyDistributions: StatsCompetencyBoxplot[]) {
    this.defineCustomCompetencies();

    const nextNumber: number = this.series.length / 2 + 1;
    const data: any = this.prepareBoxplotData(statsCompetencyDistributions);

    const serialNumber = `${this.serialNumberPrefix}-${nextNumber}`;

    this.subTableAvgAndSd[serialNumber] = this.prepareBoxplotSubTableData(statsCompetencyDistributions);
    this.createSubTableColumns(nextNumber);

    this.createSubTableData(data, nextNumber);

    this.series.push({
      name: `${this.serialNumberPrefix}-${nextNumber}`,
      type: 'boxplot',
      data: data.boxData,
      tooltip: {
        formatter: function(param: any) {
          const line1: string = i18n.t('COMPETENCY_NAME').toString()+ ` : ${param.name}`;
          const line2: string = i18n.t('VALUE_MAX').toString() + ` : ${param.data[5]}`;
          const line3: string = i18n.t('MEDIAN').toString() + ` : ${param.data[3]}`;
          const line4: string = i18n.t('VALUE_MIN').toString() + ` : ${param.data[1]}`;
          const line5: string = i18n.t('QUARTILE_RANGE').toString() + ` : ${param.data[2]}〜${param.data[4]}`;
          return [line1, line2, line3, line4, line5].join('<br/>');
        },
      },
    });
    this.series.push({
      name: `${this.serialNumberPrefix}-${nextNumber}`,
      type: 'pictorialBar',
      symbolPosition: 'end',
      symbolSize: 6,
      data: data.outliers,
      tooltip: {
        formatter: function(param: any) {
          const line1: string = i18n.t('COMPETENCY_NAME').toString() + ` : ${param.name}`;
          const line2: string = i18n.t('NAME').toString() + ` : ${param.data[2]}`;
          const line3: string = i18n.t('SCORE').toString() + ` : ${param.data[1]}`;
          return [line1, line2, line3].join('<br/>');
        },
      },
      barGap: '30%',
    });
  }

  defineCustomCompetencies() {
    this.otherCompetencys = this.customCompetencies.map(cc => {
      return {
        competencyID: cc.id,
        name: cc.name,
        colorCode: '#000000',
      };
    });
  }

  prepareBoxplotData(statsCompetencyDistributions: StatsCompetencyBoxplot[]) {
    const self: any = this;

    const scoresByCompetencyID: any = statsCompetencyDistributions.reduce((result: any, distributionOfStudent: StatsCompetencyBoxplot) => {
      //その他のコンピテンシー の場合は、competencyScoresではなく、otherCompetencyScores を元にデータを作成する
      distributionOfStudent.customCompetencyScores(self.customCompetencies).forEach((score: number, competencyID: number) => {
        // スコアが 0 の場合は集計対象としない
        if (score > 0) {
          if (!result.hasOwnProperty(competencyID)) {
            result[competencyID] = [];
          }
          result[competencyID].push({
            score: score,
            name: distributionOfStudent.student.fullName,
            studentID: distributionOfStudent.student.studentID,
            belongs: distributionOfStudent.student.gradeAndSchoolClassName,
          } as ScoreProperty);
        }
      });
      return result;
    }, {});

    const ascScoreDimension: ScoreProperty[][] = this.ohterCompetencyIDs
      .reduce((result: number[][], index: number) => {
        if (scoresByCompetencyID.hasOwnProperty(index)) {
          result.push(scoresByCompetencyID[index]);
        } else {
          result.push([]);
        }
        return result;
      }, [])
      .map((rowScores: any[]) => {
        rowScores.sort(function (a, b) {
          return a.score - b.score;
        });
        return rowScores;
      });

    const dataTool: any = DataTool;
    let chartData: any = dataTool.prepareBoxplotData(ascScoreDimension);

    let outliers: any[][] = [];

    const result: any = ascScoreDimension.reduce(
      (result: any, studentScores: ScoreProperty[], index: number) => {
        result.axisData.push(String(index));
        if (studentScores.length === 0) {
          result.boxData.push([]);
          return result;
        }

        let measurementLowScore: number;
        let measurementHighScore: number;
        const q1 = self.quantile(studentScores, 0.25);
        const q2 = self.quantile(studentScores, 0.5);
        const q3 = self.quantile(studentScores, 0.75);
        const min = studentScores[0].score;
        const max = studentScores[studentScores.length - 1].score;
        const bound = 1.5 * (q3 - q1);
        const virtualLow = Math.max(min, q1 - bound);
        const virtualHigh = Math.min(max, q3 + bound);

        if (virtualLow === min) {
          measurementLowScore = min;
        } else {
          const nextMeasurementLowScoreIndex: number = studentScores.findIndex((studentScore: ScoreProperty) => {
            // 小→大に検索
            if (studentScore.score <= virtualLow) {
              outliers.push([index, self.percent(studentScore.score), studentScore.name, studentScore.studentID]);
              return false;
            }
            // 最小値を上回ったら終了
            return true;
          });
          // 最後の値を最大値とするため、外れ値から除外
          outliers.pop();
          measurementLowScore = studentScores[nextMeasurementLowScoreIndex - 1].score;
        }

        if (virtualHigh === max) {
          measurementHighScore = max;
        } else {
          let i: number = studentScores.length - 1;
          for (; i > -1; i--) {
            // 大→小に検索
            if (studentScores[i].score >= virtualHigh) {
              outliers.push([index, self.percent(studentScores[i].score), studentScores[i].name, studentScores[i].studentID]);
              continue;
            }
            // 最大値を下回ったら終了
            break;
          }
          // 最後の値を最大値とするため、外れ値から除外
          outliers.pop();
          measurementHighScore = studentScores[i + 1].score;
        }

        result.boxData.push([measurementLowScore, q1, q2, q3, measurementHighScore].map((v: number) => self.percent(v)));

        return result;
      },
      { boxData: [], axisData: [] }
    );

    result.outliers = outliers;

    return result;
  }

  prepareBoxplotSubTableData(statsCompetencyDistributions: StatsCompetencyBoxplot[]) {
    const self: any = this;
    const scoresByCompetencyID: any = statsCompetencyDistributions.reduce((result: any, distributionOfStudent: StatsCompetencyBoxplot) => {
      //その他のコンピテンシー の場合は、competencyScoresではなく、otherCompetencyScores を元にデータを作成する
      distributionOfStudent.customCompetencyScores(self.customCompetencies).forEach((score: number, competencyID: number) => {
        // スコアが 0 の場合は集計対象としない
        if (score > 0) {
          if (!result.hasOwnProperty(competencyID)) {
            result[competencyID] = [];
          }
          result[competencyID].push({
            score: score,
            name: distributionOfStudent.student.fullName,
            belongs: distributionOfStudent.student.gradeAndSchoolClassName,
          } as ScoreProperty);
        }
      });
      return result;
    }, {});

    let avgAndSds: AvgAndSds = {};
    for (const competencyID in scoresByCompetencyID) {
      const scoresAarray: number[] = scoresByCompetencyID[competencyID].map((d: ScoreProperty) => d.score);
      const avg: number = NumberUtils.averageFromArray(scoresAarray);
      const avgAndSd: AvgAndSd = {
        avg: avg,
        sd: NumberUtils.standardDeviation(scoresAarray, avg),
        n: scoresAarray.length,
      };
      avgAndSds[competencyID] = avgAndSd;
    }
    return avgAndSds;
  }

  quantile(ascArr: ScoreProperty[], p: number): number {
    const H = (ascArr.length - 1) * p + 1;
    const h = Math.floor(H);
    const v = +ascArr[h - 1].score;
    const e = H - h;
    return e ? v + e * (ascArr[h].score - v) : v;
  }

  percent(d: number) {
    return Math.floor(d * 100);
  }
}
